import { FormikValues, useFormik } from 'formik'
import { FC, FormEvent, Fragment, useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { mainport } from 'src/constants'
import { shoot } from 'src/redux/actions'
import { select_formik_pool } from 'src/redux/store'
import { fitem_t, FORMIK_WIDTHS, formset_t } from 'src/types'
import {
  compare_dates,
  deep_cmp,
  deep_copy,
  fill_placeholders,
  filter_fstep,
  find_fitem_idx,
  insensitive_compare,
  is_obj_full,
  join_str,
  mop_2_ruote_categories,
  sort_dependencies,
  to_date,
  to_formik_schema,
  update_html_field,
} from 'src/utils'
import { object as yup_object } from 'yup'
import { ObjectShape } from 'yup/lib/object'

import { CheckBox } from '../check-box/check-box'
import { DatePicker_ } from '../date-picker/date-picker'
import { InputText } from '../input-text/input-text'
import { PrivacyAccordion } from '../privacy-accordion/privacy-accordion'
import { RadioGroup } from '../radio-group/radio-group'
import { SelectMenu } from '../select-menu/select-menu'
import { Tooltip } from '../tooltip/tooltip'

export const FormSet: FC<formset_t> = ({
  step = '',
  fired: fired_ = false,
  disabled,
  classes = mainport,
  on_change,
  on_submit: on_submit_,
  children,
}: formset_t) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  /**
   * Use Selector
   */
  const fpool = useSelector(select_formik_pool) || []
  const fstep = filter_fstep(fpool, step)

  /**
   * Use State
   */
  const [ivalues] = useState<FormikValues>({})
  const [ischema, set_ischema] = useState<ObjectShape>({})
  const [valid, set_valid] = useState(false)
  const [fired, set_fired] = useState(false)
  const [alive, set_alive] = useState(true)
  const [gtm_class, set_gtm_class] = useState('')
  const [category_change, set_category_change] = useState(false)

  /**
   * Use Formik
   */
  const {
    handleSubmit,
    setFieldValue,
    setSubmitting,
    isSubmitting,
    isValid,
    validateForm,
    errors,
    values,
  } = useFormik({
    initialValues: ivalues,
    validationSchema: yup_object(ischema),
    onSubmit: () => {
      //
    },
  })

  /**
   * Use Effect
   */
  useEffect(() => {
    return () => set_alive(false)
  }, [])

  useEffect(() => {
    init_pool()
  }, [fpool])

  useEffect(() => {
    const idx = find_fitem_idx(fpool, 'ts2__mop__category')
    if (
      step === 'ts2' &&
      !window.location.pathname.includes('generazione') &&
      values?.ts2__mop__category !== fpool?.[idx]?.capture
    ) {
      set_category_change(true)
      setTimeout(() => {
        refresh_pool()
      }, 100)
    } else {
      refresh_pool()
    }

    if (alive) {
      setTimeout(validateForm)
    }
  }, [fpool, values, alive])

  useEffect(() => {
    set_fired(fired_)
  }, [fired_])

  useEffect(() => {
    set_valid(isValid && !isSubmitting)
  }, [fired, isValid, isSubmitting])

  useEffect(() => {
    on_change?.({ fpool, fired, valid, gtm_class })
  }, [fpool, fired, valid, gtm_class])

  /**
   * Support functions
   */
  const on_submit = (event?: FormEvent<HTMLFormElement>) => {
    handleSubmit(event)
    setSubmitting(false)
    set_fired(true)
    on_submit_?.({ fpool, fired, valid })
  }

  const init_pool = () => {
    if (fpool) {
      set_ischema(to_formik_schema(fstep))
    }
  }

  const on_change_gtm_class = (gtm_class: string) => {
    set_gtm_class(gtm_class)
  }

  const refresh_pool = () => {
    if (fpool && is_obj_full(values)) {
      const newpool = deep_copy(fpool)

      const idxVehicleType = find_fitem_idx(newpool, 'ts1a__vehicle_type')

      fstep
        ?.filter(({ hidden }) => !hidden)
        ?.forEach((fitem_) => {
          const changed = fitem_?.capture !== values?.[fitem_?.id]

          // Update current capture
          if (changed) {
            const idx = find_fitem_idx(newpool, fitem_?.id)
            if (idx !== -1) {
              newpool[idx].capture = values?.[fitem_?.id]
            }
          }

          // Update dependent items
          for (const dkey of sort_dependencies(fitem_?.dependencies)) {
            if (
              dkey === '*' ||
              (dkey === 'onchange' && changed) ||
              insensitive_compare(dkey, values?.[fitem_?.id])
            ) {
              if (newpool?.[idxVehicleType]?.capture === '1001-5') {
                adjust_mop_dependencies(fitem_)
              }

              // From current item dependencies...
              const dval = fitem_?.dependencies?.[dkey] || {}
              const { hidden: hid, disabled: dis, precompiled: pre, domains: dom } = dval
              const k_dis = Object.keys(dis || {})
              const k_hid = Object.keys(hid || {})
              const k_pre = Object.keys(pre || {})
              const k_dom = Object.keys(dom || {})

              // ...to new mapped array
              k_dis?.forEach((key_) => {
                const idx = find_fitem_idx(newpool, key_)
                if (idx !== -1) {
                  newpool[idx].disabled = dis?.[key_]
                }
              })
              k_hid?.forEach((key_) => {
                const idx = find_fitem_idx(newpool, key_)
                if (idx !== -1) {
                  newpool[idx].hidden = hid?.[key_]
                }
              })
              k_pre?.forEach((key_) => {
                const idx = find_fitem_idx(newpool, key_)
                if (idx !== -1) {
                  newpool[idx].capture = pre?.[key_]
                }
              })
              k_dom?.forEach((key_) => {
                const idx = find_fitem_idx(newpool, key_)
                if (idx !== -1) {
                  newpool[idx].options = dom?.[key_][fitem_?.capture || '']
                }
              })
            }
          }
        })

      // Dispatch new formik pool
      if (!deep_cmp(fpool, newpool)) {
        dispatch(shoot.reduce_formik_pool(newpool))
      }
    }
  }

  const adjust_mop_dependencies = (fitem_: fitem_t) => {
    const seleziona4R = document.getElementById('ts2__mop__4__ruote_') as HTMLInputElement
    const selezionaCarrozzerie = document.getElementById(
      'ts2__mop__carrozzerie__type_'
    ) as HTMLInputElement
    const seleziona2R = document.getElementById('ts2__mop__2__ruote_') as HTMLInputElement
    const selezionaVeicoli = document.getElementById('ts2__mop__vehicle__type_') as HTMLInputElement
    const mopCategory = document.getElementById('ts2__mop__category') as HTMLInputElement
    const carrozzerie = document.getElementById('ts2__mop__carrozzerie__type') as HTMLInputElement
    const veicoli = document.getElementById('ts2__mop__vehicle__type') as HTMLInputElement
    if (
      fitem_?.id === 'ts2__mop__category' &&
      fitem_?.capture !== '' &&
      category_change &&
      !window.location.pathname.includes('generazione')
    ) {
      set_category_change(false)

      seleziona4R?.click()
      selezionaCarrozzerie?.click()
      seleziona2R?.click()
      selezionaVeicoli?.click()
      if (mop_2_ruote_categories.includes(fitem_?.capture || '')) {
        update_html_field('ts2__mop__4__ruote', 'disable')
        update_html_field('ts2__mop__carrozzerie__type', 'disable')
        update_html_field('ts2__mop__2__ruote', 'enable')
      } else {
        update_html_field('ts2__mop__4__ruote', 'enable')
      }
    } else if (fitem_?.id === 'ts2__mop__2__ruote' && fitem_?.capture !== '') {
      seleziona4R?.click()
      update_html_field('ts2__mop__4__ruote', 'disable')
      selezionaCarrozzerie?.click()
      update_html_field('ts2__mop__carrozzerie__type', 'disable')
    } else if (fitem_?.id === 'ts2__mop__2__ruote' && fitem_?.capture === '') {
      if (!mop_2_ruote_categories.includes(mopCategory?.value || '')) {
        update_html_field('ts2__mop__4__ruote', 'enable')
      }
      selezionaVeicoli?.click()

      if ((document.getElementById('ts2__mop__4__ruote') as HTMLInputElement)?.value === '') {
        update_html_field('ts2__mop__2__ruote', 'enable')
      }
    } else if (fitem_?.id === 'ts2__mop__4__ruote' && fitem_?.capture === '') {
      update_html_field('ts2__mop__carrozzerie__type', 'disable')
      selezionaCarrozzerie?.click()
    } else if (fitem_?.id === 'ts2__mop__4__ruote' && fitem_?.capture !== '') {
      update_html_field('ts2__mop__2__ruote', 'disable')
      if (
        !mop_2_ruote_categories.includes(
          (document.getElementById('ts2__mop__category') as HTMLInputElement)?.value
        )
      ) {
        update_html_field('ts2__mop__carrozzerie__type', 'enable')
      }
    } else if (fitem_?.id === 'ts2__mop__vehicle_power_supply' && fitem_?.capture !== '') {
      const mopCategory = document.getElementById('ts2__mop__category') as HTMLInputElement
      if (mop_2_ruote_categories.includes(mopCategory?.value || ''))
        update_html_field('ts2__mop__4__ruote', 'disable')
    } else if (
      fitem_?.id === 'ts2__mop__carrozzerie__type' ||
      fitem_?.id === 'ts2__mop__vehicle__type'
    ) {
      veicoli?.value === '' && carrozzerie?.value === ''
        ? update_html_field('ts2__mop__maximum_per_claim', 'disable')
        : update_html_field('ts2__mop__maximum_per_claim', 'enable')
    }
  }

  const resume_fitem = (fitem_: fitem_t) => {
    const fitem_cpy = {
      ...deep_copy(fitem_),
      disabled: disabled || fitem_?.disabled,
      error: String(errors?.[fitem_?.id] || ''),
      fired,
      classes: 'w-100',
      set_field_value: setFieldValue,
    }
    if (
      fitem_cpy.id?.includes('is1__vehicle_type') ||
      fitem_cpy.id?.includes('is3__plate_present')
    ) {
      fitem_cpy.options = fitem_cpy.options?.map(({ key, gtm_class }) => ({
        key,
        val: t(`is1.${key}`),
        gtm_class,
        on_change_gtm_class,
      }))
    }
    if (fitem_cpy.id?.includes('ts2__car__vehicle_absent') && fitem_cpy.capture !== 'true') {
      fitem_cpy.description = ''
    }
    if (fitem_cpy.id?.includes('__year_') && fitem_cpy.title) {
      fitem_cpy.title = fill_placeholders(
        fitem_cpy.title,
        `${fstep?.filter(({ id }) => id.includes('__year_'))?.length || 0}`
      )
    }
    if (fitem_cpy.id?.includes('__year_') && fitem_cpy.description) {
      fitem_cpy.description = fill_placeholders(
        fitem_cpy.description,
        `${fstep?.filter(({ id }) => id.includes('__year_'))?.length || 0}`
      )
    }
    if (fitem_cpy.id === 'ts1a__effective_date') {
      const error_date = new Date()
      error_date.setMonth(error_date.getMonth() + 12)
      const current_date = to_date(fitem_?.capture)
      if (compare_dates(current_date, error_date) === 1) {
        document?.querySelector?.('button[type="submit"]')?.setAttribute('disabled', 'true')
      } else {
        document?.querySelector?.('button[type="submit"]')?.removeAttribute('disabled')
      }
    }
    return fitem_cpy
  }

  /**
   * Render functions
   */
  const render_title = ({ title }: fitem_t) => {
    return title && <p className="col-12 mb-n1 font-20 c-7f7f7f">{title}</p>
  }

  const render_tooltip = ({ tooltip }: fitem_t) => {
    return (
      tooltip && (
        <Tooltip classes="mt-tooltip" icon_size={18}>
          <div>{tooltip}</div>
        </Tooltip>
      )
    )
  }

  const render_description = ({ description, classes_description }: fitem_t) => {
    return description && <p className={`col-12 font-16 ${classes_description}`}>{description}</p>
  }

  const render_privacy = ({ privacy }: fitem_t) => {
    return (
      privacy && (
        <div className="col-12">
          <PrivacyAccordion />
        </div>
      )
    )
  }

  return (
    <form id={`form-${step}`} onSubmit={on_submit} className={classes}>
      <Row>
        {fstep?.map((fitem_, idx) => {
          const fitem__ = resume_fitem(fitem_)
          if (fitem__?.id === 'ts2__atx__vehicle_power') {
            const vehicle_power = document.getElementById('ts2__atx__vehicle_power')
            if (vehicle_power) {
              values[fitem_.id] = vehicle_power?.getAttribute('value')
            }
          }
          if (fitem__?.id === 'ts2__atx__vehicle_displacement') {
            const vehicle_displacement = document.getElementById('ts2__atx__vehicle_displacement')
            if (vehicle_displacement) {
              values[fitem_.id] = vehicle_displacement?.getAttribute('value')
            }
          }
          return (
            <Fragment key={idx}>
              {fitem_.hidden ? (
                <></>
              ) : (
                <>
                  {render_title(fitem__)}

                  <Col className={join_str('col-12 my-2', FORMIK_WIDTHS[fitem__?.width || 100])}>
                    <div className="d-flex">
                      {fitem__?.type === 'INPUT_TEXT' && <InputText {...fitem__} />}
                      {fitem__?.type === 'SELECT_MENU' && <SelectMenu {...fitem__} />}
                      {fitem__?.type === 'CHECK_BOX' && <CheckBox {...fitem__} />}
                      {fitem__?.type === 'RADIO_GROUP' && <RadioGroup {...fitem__} />}
                      {fitem__?.type === 'DATE_PICKER' && <DatePicker_ {...fitem__} />}
                      {render_tooltip(fitem__)}
                    </div>
                  </Col>

                  {render_description(fitem__)}
                  {render_privacy(fitem__)}
                </>
              )}
            </Fragment>
          )
        })}
        <div className="col-12">{children?.({ fpool, fired, valid })}</div>
      </Row>
    </form>
  )
}
